import { ChangeEvent } from 'react';
import { CoreBox, CoreCheckbox, CoreFormControlLabel, CoreTypography } from '@youscience/core';
import { FinalReportRequest, YesNo, FinalReportSchema } from '@youscience/commonapp-service-common';
import { FormHelperText } from '@mui/material';

import { FormState } from '@constants/commonApp/types';
import { REQUIRED_FIELD_MESSAGE } from '@constants/texts';

import { TabSections } from '@interfaces/recommenderWorkspace';

import ContinueButton from '@components/ContinueButton';

import FileUpload from '../../../../common/FileUpload';
import SectionAccordion from '../../../../../../../components/SectionAccordion';
import { getFilename } from '../../../../common/formUtils';

import { sxStyles } from './Transcripts.styles';
import { useApplicantFormSection } from '@pages/StudentDetailsPage/hooks/useApplicantFormSection';

export type TranscriptsFields = Partial<FinalReportRequest[TabSections.Transcripts]>;

interface TranscriptsProps {
  transcripts?: TranscriptsFields;
  onFormChange: (formState: FormState<TranscriptsFields>) => void;
  onExpand: () => void;
  onContinue?: () => void;
  isExtended: boolean;
  isSaved: boolean;
  applicantName?: string;
  showError?: boolean;
}

const validator = FinalReportSchema.shape.transcripts;

export const Transcripts = ({
  transcripts = {},
  onFormChange,
  onExpand,
  onContinue,
  isExtended,
  isSaved,
  applicantName = '',
  showError = false,
}: TranscriptsProps) => {
  const [formState, setFormStateWithErrors] = useApplicantFormSection<TranscriptsFields, typeof validator>(
    transcripts,
    validator,
    isSaved,
    onFormChange,
  );

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setFormStateWithErrors({ [event.target.name]: checked ? YesNo.Yes : undefined });
  };

  const handleFileUpload = (field: string) => (file: string) => {
    setFormStateWithErrors({ [field]: file });
  };

  const handleFileDelete = (field: string) => () => {
    setFormStateWithErrors({ [field]: undefined });
  };

  const isFormValid = () => {
    const validation = validator.safeParse(formState.data);
    return validation.success;
  };

  return (
    <SectionAccordion
      title="Transcripts"
      isExpanded={isExtended}
      onChange={onExpand}
      isChanged={formState.hasChanged}
      showCheckmark={formState.isSaved && isFormValid()}
      isError={showError || !isFormValid()}
    >
      <CoreBox sx={sxStyles.accordionDetails}>
        <FileUpload
          title="Attach applicant's final official transcript, including a transcript legend."
          name="final"
          initialFile={formState.data.final}
          previewTitle="Final official transcript"
          filename={formState.data.final ? getFilename(applicantName, 'final') : undefined}
          onFileUpload={handleFileUpload}
          onFileDelete={handleFileDelete}
          error={formState.errors.final}
        />

        <CoreBox sx={sxStyles.flexWrap}>
          <CoreTypography variant="subtitle2" component="h4">
            Transcript Affirmation
          </CoreTypography>

          <CoreFormControlLabel
            sx={sxStyles.checkbox}
            name="affirmation"
            label="I affirm that I have uploaded a transcript to this Final Report. I understand that failure to do so (by, for example, uploading a blank document or uploading a document promising to mail the transcript at a later time) will result in my Common App Online account being closed."
            control={
              <CoreCheckbox
                value={true}
                checked={formState.data.affirmation === YesNo.Yes}
                onChange={handleCheckboxChange}
              />
            }
          />

          {formState.errors.affirmation && <FormHelperText error>{REQUIRED_FIELD_MESSAGE}</FormHelperText>}
        </CoreBox>

        <ContinueButton onContinue={onContinue} />
      </CoreBox>
    </SectionAccordion>
  );
};
